import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Users, Search, Loader2, RefreshCw, ChevronLeft, ChevronRight,
  UserCheck, UserX, Shield, Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import adminService from '../../services/adminService';

const ROLES = [
  { id: 1001, name: 'Admin' },
  { id: 1002, name: 'BusinessOwner' },
  { id: 1003, name: 'Compliance' },
];

const EMPTY_USER_FORM = { firstName: '', lastName: '', email: '', password: '', phoneNo: '', roleId: '1001' };

export default function AdminUsersPage() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pageSize: 25, totalPages: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('all');
  const [page, setPage] = useState(1);

  const [selectedUser, setSelectedUser] = useState(null);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [newRoleId, setNewRoleId] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Create user
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_USER_FORM);
  const [isCreating, setIsCreating] = useState(false);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const filter = { search: search || undefined, page, pageSize: 25 };
      if (isActiveFilter !== 'all') filter.isActive = isActiveFilter === 'active';
      const result = await adminService.getUsers(filter);
      setData(result);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [search, isActiveFilter, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleToggleActive = async (user) => {
    try {
      if (user.isActive) {
        await adminService.deactivateUser(user.userId);
        toast.success(`${user.fullName} deactivated`);
      } else {
        await adminService.activateUser(user.userId);
        toast.success(`${user.fullName} activated`);
      }
      fetchUsers();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to update user status');
    }
  };

  const openRoleDialog = (user) => {
    setSelectedUser(user);
    setNewRoleId(String(user.roleId));
    setIsRoleDialogOpen(true);
  };

  const handleChangeRole = async () => {
    if (!selectedUser || !newRoleId) return;
    setIsSaving(true);
    try {
      await adminService.changeUserRole(selectedUser.userId, Number(newRoleId));
      toast.success('Role updated');
      setIsRoleDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to change role');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const handleCreateUser = async () => {
    if (!createForm.firstName || !createForm.lastName || !createForm.email || !createForm.password) {
      toast.error('First name, last name, email and password are required');
      return;
    }
    setIsCreating(true);
    try {
      await adminService.createUser({
        firstName: createForm.firstName,
        lastName: createForm.lastName,
        email: createForm.email,
        password: createForm.password,
        phoneNo: createForm.phoneNo || undefined,
        roleId: Number(createForm.roleId),
      });
      toast.success('User created successfully');
      setIsCreateOpen(false);
      setCreateForm(EMPTY_USER_FORM);
      fetchUsers();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to create user');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">User Management</h1>
            <p className="text-muted-foreground mt-1">Manage all registered users</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchUsers}>
              <RefreshCw className="h-4 w-4 mr-2" />Refresh
            </Button>
            <Button size="sm" onClick={() => { setCreateForm(EMPTY_USER_FORM); setIsCreateOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />Create User
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={isActiveFilter} onValueChange={v => { setIsActiveFilter(v); setPage(1); }}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit">Search</Button>
            </form>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle>Users</CardTitle>
            <CardDescription>{data.total} total users</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Organization</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.items?.map(user => (
                        <TableRow key={user.userId}>
                          <TableCell className="font-medium">{user.fullName}</TableCell>
                          <TableCell className="text-muted-foreground">{user.email}</TableCell>
                          <TableCell>{user.organizationName}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{user.roleName}</Badge>
                          </TableCell>
                          <TableCell>
                            {user.isActive
                              ? <Badge className="bg-green-100 text-green-800">Active</Badge>
                              : <Badge className="bg-red-100 text-red-800">Inactive</Badge>}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {new Date(user.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleActive(user)}
                                title={user.isActive ? 'Deactivate' : 'Activate'}
                              >
                                {user.isActive
                                  ? <UserX className="h-4 w-4 text-red-500" />
                                  : <UserCheck className="h-4 w-4 text-green-500" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openRoleDialog(user)}
                                title="Change Role"
                              >
                                <Shield className="h-4 w-4 text-blue-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {data.page} of {data.totalPages} ({data.total} records)
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" disabled={page >= data.totalPages} onClick={() => setPage(p => p + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create User Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
            <DialogDescription>Add a new user to the system.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>First Name *</Label>
                <Input value={createForm.firstName} onChange={e => setCreateForm(p => ({ ...p, firstName: e.target.value }))} placeholder="Jane" />
              </div>
              <div className="space-y-1">
                <Label>Last Name *</Label>
                <Input value={createForm.lastName} onChange={e => setCreateForm(p => ({ ...p, lastName: e.target.value }))} placeholder="Smith" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Email *</Label>
              <Input type="email" value={createForm.email} onChange={e => setCreateForm(p => ({ ...p, email: e.target.value }))} placeholder="jane@example.com" />
            </div>
            <div className="space-y-1">
              <Label>Password *</Label>
              <Input type="password" value={createForm.password} onChange={e => setCreateForm(p => ({ ...p, password: e.target.value }))} placeholder="Min 8 characters" />
            </div>
            <div className="space-y-1">
              <Label>Phone</Label>
              <Input value={createForm.phoneNo} onChange={e => setCreateForm(p => ({ ...p, phoneNo: e.target.value }))} placeholder="(555) 000-0000" />
            </div>
            <div className="space-y-1">
              <Label>Role</Label>
              <Select value={createForm.roleId} onValueChange={v => setCreateForm(p => ({ ...p, roleId: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button className="flex-1" onClick={handleCreateUser} disabled={isCreating}>
                {isCreating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                Create User
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
            <DialogDescription>{selectedUser?.fullName} — {selectedUser?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>New Role</Label>
              <Select value={newRoleId} onValueChange={setNewRoleId}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button className="flex-1" onClick={handleChangeRole} disabled={isSaving}>
                {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Save
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setIsRoleDialogOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
